import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import SafeIcon from '../../common/SafeIcon';
import RichTextEditor from './RichTextEditor';

const TemplatesSection = ({ trainerId }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [masterTemplateId, setMasterTemplateId] = useState(null);

  const initialFormState = {
    title: '',
    description: '',
    content: '', // Maps to lesson_content
    lesson_title: '',
    lesson_collapsed: true,
    demo_videos: [''],
    demo_video_title: '',
    task_checkboxes: [''],
    require_video_submission: false,
    video_submission_description: '',
    resource_url: '',
    category: 'General',
    is_rest_day: false
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (trainerId) fetchTemplates();
  }, [trainerId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data: profile } = await supabase.from('trainer_profiles').select('master_template_id').eq('id', trainerId).single();
      if (profile) setMasterTemplateId(profile.master_template_id);

      const { data, error } = await supabase
        .from('lesson_templates_20250218')
        .select('*')
        .eq('trainer_id', trainerId)
        .order('title');

      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const setAsMaster = async (templateId) => {
    try {
      const { error } = await supabase
        .from('trainer_profiles')
        .update({ master_template_id: templateId })
        .eq('id', trainerId);
      if (error) throw error;
      setMasterTemplateId(templateId);
    } catch (err) {
      alert('Failed to set master template');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const cleanData = {
        ...formData,
        trainer_id: trainerId,
        demo_videos: formData.demo_videos.filter(v => v.trim()),
        task_checkboxes: formData.task_checkboxes.filter(v => v.trim())
      };

      if (editingTemplate) {
        const { error } = await supabase
          .from('lesson_templates_20250218')
          .update(cleanData)
          .eq('id', editingTemplate.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('lesson_templates_20250218')
          .insert([cleanData]);
        if (error) throw error;
      }
      setShowForm(false);
      fetchTemplates();
    } catch (err) {
      alert('Error saving template: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      ...initialFormState,
      ...template,
      lesson_collapsed: template.lesson_collapsed !== undefined ? template.lesson_collapsed : true,
      demo_videos: template.demo_videos?.length ? template.demo_videos : [''],
      task_checkboxes: template.task_checkboxes?.length ? template.task_checkboxes : [''],
      demo_video_title: template.demo_video_title || '',
      video_submission_description: template.video_submission_description || '',
      resource_url: template.resource_url || '',
      is_rest_day: !!template.is_rest_day
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure? This template will be permanently deleted.')) return;
    try {
      await supabase.from('lesson_templates_20250218').delete().eq('id', id);
      fetchTemplates();
    } catch (err) {
      alert('Delete failed');
    }
  };

  if (loading) return <div className="p-8 animate-pulse space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-24 bg-white rounded-2xl border" />)}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div>
          <h2 className="text-2xl font-black text-gray-900">Lesson Templates</h2>
          <p className="text-sm text-gray-500 font-medium">Standardize your curriculum and onboard new clients automatically</p>
        </div>
        <button 
          onClick={() => { setEditingTemplate(null); setFormData(initialFormState); setShowForm(true); }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black transition-all flex items-center gap-2 shadow-xl shadow-indigo-100 active:scale-95"
        >
          <SafeIcon name="Plus" className="w-5 h-5" />
          New Template
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map(t => (
          <div key={t.id} className={`bg-white p-6 rounded-[2rem] border-2 shadow-sm hover:shadow-md transition-all group relative overflow-hidden ${masterTemplateId === t.id ? 'border-indigo-500' : 'border-gray-100'}`}>
            {masterTemplateId === t.id && (
              <div className="absolute top-0 right-0 bg-indigo-600 text-white px-4 py-1 rounded-bl-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                <SafeIcon name="Award" className="w-3.5 h-3.5" />
                Master Template
              </div>
            )}
            <div className="flex justify-between items-start mb-4">
              <div>
                <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest bg-indigo-50 px-2 py-0.5 rounded-md mb-2 inline-block">
                  {t.category || 'General'}
                </span>
                <h4 className="font-black text-xl text-gray-900 group-hover:text-indigo-600 transition-colors">{t.title}</h4>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setAsMaster(t.id)} className={`p-2 rounded-xl transition-all ${masterTemplateId === t.id ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`} title="Make Master Template">
                  <SafeIcon name="Award" className="w-5 h-5" />
                </button>
                <button onClick={() => handleEdit(t)} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all">
                  <SafeIcon name="Edit" className="w-5 h-5" />
                </button>
                <button onClick={() => handleDelete(t.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all">
                  <SafeIcon name="Trash2" className="w-5 h-5" />
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 line-clamp-2 font-medium mb-4">{t.description || 'No summary provided.'}</p>
            <div className="flex items-center gap-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">
              <span className="flex items-center gap-1.5"><SafeIcon name="Video" className="w-3.5 h-3.5" /> {t.demo_videos?.length || 0} Videos</span>
              <span className="flex items-center gap-1.5"><SafeIcon name="CheckSquare" className="w-3.5 h-3.5" /> {t.task_checkboxes?.length || 0} Checkpoints</span>
              {t.is_rest_day && <span className="flex items-center gap-1.5 text-green-600"><SafeIcon name="Clock" className="w-3.5 h-3.5" /> Rest Day</span>}
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {showForm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-[9999]" onClick={() => setShowForm(false)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-8 border-b flex justify-between items-center bg-gray-50/50">
                <div>
                  <h3 className="text-2xl font-black text-gray-900">{editingTemplate ? 'Edit Template' : 'New Template'}</h3>
                  <p className="text-sm text-gray-500 font-medium">Create a high-fidelity training module</p>
                </div>
                <button onClick={() => setShowForm(false)} className="p-3 hover:bg-gray-100 rounded-2xl transition-colors">
                  <SafeIcon name="X" className="w-6 h-6 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 space-y-10">
                <section className="space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg"><SafeIcon name="Info" /></div>
                      <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">General Information</h4>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input type="checkbox" checked={formData.is_rest_day} onChange={e => setFormData({ ...formData, is_rest_day: e.target.checked })} className="w-5 h-5 rounded border-gray-300 text-indigo-600" />
                      <span className="text-xs font-black text-gray-400 uppercase tracking-widest group-hover:text-indigo-600 transition-colors">Template as Rest Day</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Template Name</label>
                      <input required placeholder="e.g. Master the 'Sit' Command" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Category</label>
                      <input placeholder="e.g. Obedience" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Short Summary (Optional)</label>
                    <textarea placeholder="Briefly explain the goal of this task..." value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={2} className="w-full px-4 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none resize-none font-medium transition-all" />
                  </div>
                </section>

                {!formData.is_rest_day && (
                  <>
                    <section className="space-y-4 pt-6 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg"><SafeIcon name="Book" /></div>
                          <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Training Lesson</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-gray-400 uppercase">Collapse by default</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" checked={formData.lesson_collapsed} onChange={(e) => setFormData({ ...formData, lesson_collapsed: e.target.checked })} />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                          </label>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <input placeholder="Lesson Title (e.g. The Theory of Sit)" value={formData.lesson_title} onChange={e => setFormData({ ...formData, lesson_title: e.target.value })} className="w-full px-4 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all" />
                        <RichTextEditor value={formData.content} onChange={val => setFormData({ ...formData, content: val })} placeholder="Write the full lesson instructions here..." />
                      </div>
                    </section>

                    <section className="space-y-4 pt-6 border-t border-gray-100">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-purple-50 text-purple-600 rounded-lg"><SafeIcon name="Video" /></div>
                          <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Demo Videos</h4>
                        </div>
                        <button type="button" onClick={() => setFormData({ ...formData, demo_videos: [...formData.demo_videos, ''] })} className="text-xs font-black text-indigo-600 hover:underline">+ Add Video</button>
                      </div>
                      <input placeholder="Video Section Title (e.g. Demonstration Videos)" value={formData.demo_video_title} onChange={e => setFormData({ ...formData, demo_video_title: e.target.value })} className="w-full p-4 bg-gray-50 rounded-2xl font-bold border-2 border-transparent focus:border-indigo-500 outline-none" />
                      <div className="space-y-3">
                        {formData.demo_videos.map((url, i) => (
                          <div key={i} className="flex gap-2">
                            <input placeholder="Paste video link" value={url} onChange={e => { const n = [...formData.demo_videos]; n[i] = e.target.value; setFormData({ ...formData, demo_videos: n }); }} className="flex-1 px-4 py-3 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-medium transition-all" />
                            <button type="button" onClick={() => setFormData({ ...formData, demo_videos: formData.demo_videos.filter((_, idx) => idx !== i) })} className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all"><SafeIcon name="Trash2" /></button>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section className="space-y-4 pt-6 border-t border-gray-100">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-green-50 text-green-600 rounded-lg"><SafeIcon name="CheckSquare" /></div>
                          <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Training Checklist</h4>
                        </div>
                        <button type="button" onClick={() => setFormData({ ...formData, task_checkboxes: [...formData.task_checkboxes, ''] })} className="text-xs font-black text-indigo-600 hover:underline">+ Add Step</button>
                      </div>
                      {formData.task_checkboxes.map((cb, i) => (
                        <div key={i} className="flex gap-2">
                          <input placeholder={`Step ${i + 1}`} value={cb} onChange={e => { const n = [...formData.task_checkboxes]; n[i] = e.target.value; setFormData({ ...formData, task_checkboxes: n }); }} className="flex-1 p-3 bg-gray-50 rounded-xl border-2 border-transparent focus:border-indigo-500 outline-none font-medium" />
                          <button type="button" onClick={() => setFormData({ ...formData, task_checkboxes: formData.task_checkboxes.filter((_, idx) => idx !== i) })} className="p-3 text-gray-400 hover:text-red-500 transition-colors"><SafeIcon name="Trash2" /></button>
                        </div>
                      ))}
                    </section>

                    <section className="space-y-4 pt-6 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg"><SafeIcon name="Link" /></div>
                        <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">External Resources</h4>
                      </div>
                      <input placeholder="External Resource URL (e.g. link to a blog post or PDF)" value={formData.resource_url} onChange={e => setFormData({ ...formData, resource_url: e.target.value })} className="w-full p-4 bg-gray-50 rounded-2xl font-bold border-2 border-transparent focus:border-indigo-500 outline-none" />
                    </section>

                    <section className="space-y-4 pt-6 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-orange-50 text-orange-600 rounded-lg"><SafeIcon name="Video" /></div>
                        <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Submission Requirements</h4>
                      </div>
                      <label className="flex items-center gap-4 bg-gray-50 p-6 rounded-[2rem] border border-gray-100 shadow-inner cursor-pointer">
                        <input type="checkbox" checked={formData.require_video_submission} onChange={e => setFormData({ ...formData, require_video_submission: e.target.checked })} className="w-6 h-6 rounded-lg text-indigo-600 border-gray-300" />
                        <div>
                          <span className="block font-black text-gray-900">Require Video Submission</span>
                          <span className="block text-xs text-gray-500 font-medium">Owner must upload a video of this task</span>
                        </div>
                      </label>
                      {formData.require_video_submission && (
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Submission Instructions</label>
                          <RichTextEditor value={formData.video_submission_description} onChange={val => setFormData({ ...formData, video_submission_description: val })} placeholder="Tell the owner what to film..." />
                        </div>
                      )}
                    </section>
                  </>
                )}
              </form>

              <div className="p-8 border-t bg-gray-50 flex gap-4">
                <button onClick={handleSubmit} disabled={isSubmitting} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-2xl font-black shadow-xl shadow-indigo-100 transition-all transform active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
                  {isSubmitting ? <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin" /> : 'Save Template'}
                </button>
                <button onClick={() => setShowForm(false)} className="flex-1 bg-white border-2 border-gray-100 py-4 rounded-2xl font-black text-gray-500 hover:bg-gray-100 transition-all">Cancel</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TemplatesSection;